import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RefreshCw, Ban, Check, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon, AlertCircle, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { normalizeWord, normalizeClue } from "@/lib/textUtils";

interface OldWord {
  id: number;
  word: string;
  clues: string[];
}

interface FetchResponse {
  words: OldWord[];
  totalInOldDb: number;
  totalWordsInSupabase: number;
  totalCluesInSupabase: number;
  matchedWordsInBatch: number;
  blockedCount: number;
  offset: number;
  limit: number;
}

interface ImportProgress {
  current: number;
  total: number;
  currentWord: string;
  phase: 'words' | 'clues' | 'bidirectional';
  importedWords: number;
  importedClues: number;
  errors: number;
  failedItems: { word: string; clue?: string; error: string }[];
}

const LANGUAGE_OPTIONS = [
  { value: 1, label: '🇸🇪 Svenska' },
  { value: 2, label: '🇬🇧 Engelska' },
  { value: 4, label: '🇩🇪 Tyska' },
  { value: 8, label: '🇫🇷 Franska' },
  { value: 16, label: '🇪🇸 Spanska' },
];

export function WordImportAdmin() {
  const [words, setWords] = useState<OldWord[]>([]);
  const [selectedWords, setSelectedWords] = useState<Set<number>>(new Set());
  const [selectedClues, setSelectedClues] = useState<Set<string>>(new Set());
  const [expandedWords, setExpandedWords] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [stats, setStats] = useState({ totalInOldDb: 0, totalWordsInSupabase: 0, totalCluesInSupabase: 0, blockedCount: 0 });
  const [offset, setOffset] = useState(0);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [limit, setLimit] = useState(100);
  const [selectedLanguage, setSelectedLanguage] = useState(1); // Default Swedish
  const [maxClues, setMaxClues] = useState(-1); // -1 = no filter

  // Limit options - allow up to 10000 for all searches
  const limitOptions = [100, 500, 1000, 2000, 5000, 10000];

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setOffset(0); // Reset to first page on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Words are now filtered server-side, no client-side filtering needed
  const filteredWords = words;

  const getClueKey = (wordId: number, clueIndex: number) => `${wordId}-${clueIndex}`;

  const extractWordsFromClue = (clue: string): string[] => {
    // First remove accents, then split on non-Swedish letters
    const normalized = clue
      .replace(/[éèêë]/gi, 'e')
      .replace(/[áàâã]/gi, 'a')
      .replace(/[íìîï]/gi, 'i')
      .replace(/[óòôõ]/gi, 'o')
      .replace(/[úùûü]/gi, 'u')
      .replace(/[ýÿ]/gi, 'y')
      .replace(/[ñ]/gi, 'n')
      .replace(/[ç]/gi, 'c');
    const words = normalized.toUpperCase().split(/[^A-ZÅÄÖ]+/).filter(w => w.length >= 2);
    return [...new Set(words)];
  };

  const fetchWords = async () => {
    setLoading(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.access_token) {
        toast.error('Du måste vara inloggad för att använda denna funktion. Prova att logga in igen.');
        setLoading(false);
        return;
      }
      
      const searchParam = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';
      const maxCluesParam = maxClues >= 0 ? `&maxClues=${maxClues}` : '';
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-old-words?offset=${offset}&limit=${limit}${searchParam}${maxCluesParam}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          toast.error('Din session har gått ut. Prova att logga ut och in igen.');
          return;
        }
        throw new Error(errorData.error || 'Failed to fetch words');
      }

      const result: FetchResponse = await response.json();
      setWords(result.words);
      setStats({
        totalInOldDb: result.totalInOldDb,
        totalWordsInSupabase: result.totalWordsInSupabase,
        totalCluesInSupabase: result.totalCluesInSupabase,
        blockedCount: result.blockedCount,
      });
      setSelectedWords(new Set());
      // Auto-select all clues by default
      const allClueKeys = new Set<string>();
      result.words.forEach(word => {
        word.clues.forEach((_, index) => {
          allClueKeys.add(`${word.id}-${index}`);
        });
      });
      setSelectedClues(allClueKeys);
      setExpandedWords(new Set());
    } catch (error: any) {
      console.error('Error fetching words:', error);
      toast.error(`Kunde inte hämta ord: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWords();
  }, [offset, debouncedSearch, limit, maxClues]);

  const toggleWordExpanded = (id: number) => {
    setExpandedWords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const toggleWord = (id: number) => {
    setSelectedWords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const toggleClue = (wordId: number, clueIndex: number) => {
    const key = getClueKey(wordId, clueIndex);
    setSelectedClues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  };

  const selectAllCluesForWord = (wordId: number, clues: string[]) => {
    setSelectedClues(prev => {
      const newSet = new Set(prev);
      clues.forEach((_, index) => newSet.add(getClueKey(wordId, index)));
      return newSet;
    });
  };

  const deselectAllCluesForWord = (wordId: number, clues: string[]) => {
    setSelectedClues(prev => {
      const newSet = new Set(prev);
      clues.forEach((_, index) => newSet.delete(getClueKey(wordId, index)));
      return newSet;
    });
  };

  const selectAll = () => setSelectedWords(new Set(words.map(w => w.id)));
  const deselectAll = () => { setSelectedWords(new Set()); setSelectedClues(new Set()); };
  const getSelectedCluesCount = () => selectedClues.size;

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const safeDbOperation = async <T,>(
    operation: () => PromiseLike<{ data: T | null; error: any }>,
    maxRetries: number = 5
  ): Promise<{ data: T | null; error: any }> => {
    let lastError: any = null;
    for (let i = 0; i < maxRetries; i++) {
      try {
        const result = await operation();
        if (!result.error) return result;
        lastError = result.error;
        
        // Check if it's a network error that's worth retrying
        const errorMessage = String(result.error?.message || result.error || '').toLowerCase();
        const isNetworkError = errorMessage.includes('load failed') || 
                               errorMessage.includes('network') || 
                               errorMessage.includes('fetch') ||
                               errorMessage.includes('timeout') ||
                               errorMessage.includes('failed to fetch') ||
                               errorMessage.includes('typeerror');
        
        if (!isNetworkError && i === 0) {
          // If it's not a network error, don't retry
          return result;
        }
        console.warn(`DB operation attempt ${i + 1}/${maxRetries} failed (will retry):`, errorMessage);
      } catch (error: any) {
        lastError = error;
        const errorMessage = String(error?.message || error || '').toLowerCase();
        console.warn(`DB operation attempt ${i + 1}/${maxRetries} threw error:`, errorMessage);
        
        // Check if it's worth retrying
        const isNetworkError = errorMessage.includes('load failed') || 
                               errorMessage.includes('network') || 
                               errorMessage.includes('fetch') ||
                               errorMessage.includes('timeout') ||
                               errorMessage.includes('typeerror');
        
        if (!isNetworkError && i === 0) {
          return { data: null, error: { message: error?.message || 'Unknown error' } };
        }
      }
      if (i < maxRetries - 1) {
        const waitTime = Math.min(1000 * Math.pow(2, i), 8000); // Exponential backoff, max 8s
        console.log(`Retrying in ${waitTime}ms...`);
        await delay(waitTime);
      }
    }
    return { data: null, error: lastError };
  };

  const safeSupabaseCall = async <T,>(
    operation: () => PromiseLike<{ data: T | null; error: any }>,
    context: string
  ): Promise<{ data: T | null; error: any }> => {
    try {
      return await safeDbOperation(operation);
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error';
      console.error(`Error in ${context}:`, errorMessage);
      
      // Handle "Load failed" specifically
      if (errorMessage.includes('Load failed')) {
        return { 
          data: null, 
          error: { message: `Nätverksfel (${context}): Anslutningen misslyckades. Kontrollera din internetanslutning.` } 
        };
      }
      
      return { data: null, error: { message: errorMessage } };
    }
  };

  const importSelected = async () => {
    if (selectedWords.size === 0) {
      toast.info('Välj minst ett ord att importera');
      return;
    }

    // Check authentication first
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session?.access_token) {
      toast.error('Du måste vara inloggad för att importera. Prova att logga ut och in igen.');
      return;
    }

    setImporting(true);
    
    // Only process selected words - clues are only imported for selected words
    const wordsToProcess = words.filter(word => selectedWords.has(word.id));
    
    const totalClues = wordsToProcess.reduce((acc, word) => {
      const wordClueKeys = word.clues.map((_, idx) => getClueKey(word.id, idx));
      return acc + wordClueKeys.filter(key => selectedClues.has(key)).length;
    }, 0);
    
    let importedWords = 0;
    let importedClues = 0;
    let errors = 0;
    const failedItems: { word: string; clue?: string; error: string }[] = [];
    
    const updateProgress = async (current: number, total: number, currentWord: string, phase: 'words' | 'clues' | 'bidirectional') => {
      setImportProgress({
        current,
        total,
        currentWord,
        phase,
        importedWords,
        importedClues,
        errors,
        failedItems,
      });
      // Use requestAnimationFrame + delay to ensure browser renders the update
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 16); // ~1 frame at 60fps
        });
      });
    };
    
    await updateProgress(0, wordsToProcess.length + totalClues, '', 'words');

    try {
      const wordIdMap = new Map<number, number>();
      
      // Phase 1: Process words in batches
      const BATCH_SIZE = 10;
      for (let i = 0; i < wordsToProcess.length; i += BATCH_SIZE) {
        // Re-check session periodically to catch expiration early
        if (i > 0 && i % 50 === 0) {
          const { data: { session: currentSession }, error: sessionCheckError } = await supabase.auth.getSession();
          if (sessionCheckError || !currentSession) {
            const errorMsg = 'Session avslutades under import. Logga in igen och fortsätt.';
            failedItems.push({ word: 'SESSION', error: errorMsg });
            errors++;
            toast.error(errorMsg);
            throw new Error(errorMsg);
          }
        }
        
        const batch = wordsToProcess.slice(i, i + BATCH_SIZE);
        
        // First check which words already exist (use normalized word for lookup)
        const wordUppers = batch.map(w => normalizeWord(w.word).toUpperCase());
        const { data: existingWords, error: existingError } = await supabase
          .from('words')
          .select('id, word_upper')
          .in('word_upper', wordUppers);
        
        if (existingError) {
          console.error('Error fetching existing words:', existingError);
          if (existingError.message.includes('JWT') || existingError.code === 'PGRST301') {
            const errorMsg = `Session-fel: ${existingError.message}. Logga in igen.`;
            failedItems.push({ word: 'SESSION', error: errorMsg });
            errors++;
            toast.error(errorMsg);
            throw new Error(errorMsg);
          }
        }
        
        const existingMap = new Map(existingWords?.map(w => [w.word_upper, w.id]) || []);
        
        // Also fetch existing words' language to potentially update
        const existingWordIds = existingWords?.map(w => w.id) || [];
        let existingLanguageMap = new Map<number, number>();
        if (existingWordIds.length > 0) {
          const { data: existingWithLang } = await supabase
            .from('words')
            .select('id, language')
            .in('id', existingWordIds);
          existingLanguageMap = new Map(existingWithLang?.map(w => [w.id, w.language]) || []);
        }
        
        // Prepare new words to insert (use normalized word for check)
        const newWords = batch.filter(w => !existingMap.has(normalizeWord(w.word).toUpperCase()));
        
        if (newWords.length > 0) {
          const { data: inserted, error } = await supabase
            .from('words')
            .insert(newWords.map(w => {
              const normalized = normalizeWord(w.word);
              return {
                word: normalized,
                word_upper: normalized.toUpperCase(),
                length: normalized.length,
                language: selectedLanguage,
              };
            }))
            .select('id, word_upper');
          
          if (error) {
            console.error('Error inserting words:', error);
            // Check if it's a session/auth error
            if (error.message.includes('JWT') || error.code === 'PGRST301' || error.code === '42501') {
              const errorMsg = `Rättighetsfel vid insert: ${error.message}. Kan bero på utgången session.`;
              failedItems.push({ word: newWords.map(w => w.word).join(', '), error: errorMsg });
              errors += newWords.length;
              toast.error(errorMsg);
              throw new Error(errorMsg);
            }
            errors += newWords.length;
            newWords.forEach(w => failedItems.push({ word: w.word, error: error.message }));
          } else if (inserted) {
            inserted.forEach(w => existingMap.set(w.word_upper, w.id));
            importedWords += inserted.length;
          }
        }
        
        // Update language for existing words if needed
        for (const w of batch) {
          const normalizedUpper = normalizeWord(w.word).toUpperCase();
          const existingId = existingMap.get(normalizedUpper);
          if (existingId && !newWords.find(nw => normalizeWord(nw.word).toUpperCase() === normalizedUpper)) {
            const currentLang = existingLanguageMap.get(existingId) || 0;
            // Check if selectedLanguage is already in the bitmask
            if ((currentLang & selectedLanguage) === 0) {
              // Add the language to the bitmask
              const newLang = currentLang | selectedLanguage;
              await supabase
                .from('words')
                .update({ language: newLang })
                .eq('id', existingId);
            }
          }
        }
        
        // Map all words (existing + new) to their IDs (use normalized word for lookup)
        batch.forEach(w => {
          const id = existingMap.get(normalizeWord(w.word).toUpperCase());
          if (id) wordIdMap.set(w.id, id);
          if (selectedWords.has(w.id) && existingMap.has(normalizeWord(w.word).toUpperCase()) && !newWords.find(nw => nw.id === w.id)) {
            // Count existing words that were selected
          }
        });
        
        await updateProgress(
          Math.min(i + BATCH_SIZE, wordsToProcess.length),
          wordsToProcess.length + totalClues,
          batch[batch.length - 1]?.word || '',
          'words'
        );
      }

      // Phase 2: Process clues one by one for better progress tracking
      let clueCount = 0;
      for (const word of wordsToProcess) {
        const wordId = wordIdMap.get(word.id);
        if (!wordId) continue;
        
        // Use normalized word for lookups
        
        const wordUpper = normalizeWord(word.word).toUpperCase();
        const selectedClueIndices = word.clues
          .map((clue, idx) => ({ clue, idx }))
          .filter(({ idx }) => selectedClues.has(getClueKey(word.id, idx)));
        
        if (selectedClueIndices.length === 0) continue;
        
        // Check existing clues for this word
        const clueUppers = selectedClueIndices.map(c => c.clue.toUpperCase());
        const { data: existingClues } = await supabase
          .from('clues')
          .select('clue_upper')
          .eq('word_id', wordId)
          .in('clue_upper', clueUppers);
        
        const existingClueSet = new Set(existingClues?.map(c => c.clue_upper) || []);
        
        // Process each clue individually for progress tracking
        const newClues: { clue: string; idx: number }[] = [];
        for (const clueItem of selectedClueIndices) {
          clueCount++;
          await updateProgress(
            wordsToProcess.length + clueCount,
            wordsToProcess.length + totalClues,
            `${word.word}: ${clueItem.clue.substring(0, 40)}...`,
            'clues'
          );
          
          if (!existingClueSet.has(clueItem.clue.toUpperCase())) {
            newClues.push(clueItem);
          }
        }
        
        // Insert new clues in batch with selected language
        if (newClues.length > 0) {
          const { error } = await supabase
            .from('clues')
            .insert(newClues.map(c => ({
              word_id: wordId,
              clue: normalizeClue(c.clue),
              clue_upper: c.clue.toUpperCase(),
              language: selectedLanguage,
            })));
          
          if (error) {
            console.error('Error inserting clues:', error);
            if (error.message.includes('JWT') || error.code === 'PGRST301' || error.code === '42501') {
              const errorMsg = `Rättighetsfel vid ledtråds-insert: ${error.message}`;
              failedItems.push({ word: word.word, error: errorMsg });
              errors++;
              toast.error(errorMsg);
              throw new Error(errorMsg);
            }
            errors++;
            failedItems.push({ word: word.word, clue: newClues[0]?.clue?.substring(0, 30), error: error.message });
          } else {
            importedClues += newClues.length;
          }
        }
        
        // Phase 3: Bidirectional linking (with retry logic for network errors)
        for (const { clue } of newClues) {
          try {
            const clueWords = extractWordsFromClue(clue);
            const isSingleWordClue = clueWords.length === 1;
            
            const clueWordsToCheck = clueWords.filter(cw => cw !== wordUpper);
            if (clueWordsToCheck.length === 0) continue;
            
            const { data: existingClueWords, error: fetchError } = await safeSupabaseCall(
              () => supabase
                .from('words')
                .select('id, word_upper')
                .in('word_upper', clueWordsToCheck),
              `hämta ord för ledtråd "${clue.substring(0, 20)}..."`
            );
            
            if (fetchError) {
              console.warn('Could not fetch clue words, skipping bidirectional:', fetchError.message);
              continue;
            }
            
            const clueWordMap = new Map(existingClueWords?.map(w => [w.word_upper, w.id]) || []);
            
            const missingClueWords = clueWordsToCheck.filter(cw => !clueWordMap.has(cw));
            if (missingClueWords.length > 0) {
              const { data: insertedClueWords, error: insertError } = await safeSupabaseCall(
                () => supabase
                  .from('words')
                  .insert(missingClueWords.map(cw => {
                    const normalized = normalizeWord(cw);
                    return {
                      word: normalized,
                      word_upper: normalized.toUpperCase(),
                      length: normalized.length,
                      language: selectedLanguage,
                    };
                  }))
                  .select('id, word_upper'),
                `skapa saknade ord från ledtråd`
              );
              
              if (!insertError && insertedClueWords) {
                insertedClueWords.forEach(w => clueWordMap.set(w.word_upper, w.id));
              }
            }
            
            if (isSingleWordClue && clueWordsToCheck.length === 1) {
              const clueWordId = clueWordMap.get(clueWordsToCheck[0]);
              if (clueWordId) {
                const { data: existingReverse, error: reverseError } = await safeSupabaseCall(
                  () => supabase
                    .from('clues')
                    .select('id')
                    .eq('word_id', clueWordId)
                    .eq('clue_upper', wordUpper)
                    .maybeSingle(),
                  `kontrollera omvänd länk`
                );
                
                if (!reverseError && !existingReverse) {
                  await safeSupabaseCall(
                    () => supabase.from('clues').insert({
                      word_id: clueWordId,
                      clue: normalizeWord(word.word),
                      clue_upper: wordUpper,
                      language: selectedLanguage,
                    }),
                    `skapa omvänd länk för ${word.word}`
                  );
                }
              }
            }
          } catch (bidirectionalError: any) {
            // Log but don't fail the entire import for bidirectional linking errors
            console.warn(`Bidirectional linking failed for clue "${clue.substring(0, 30)}...":`, bidirectionalError?.message);
            failedItems.push({ 
              word: word.word, 
              clue: clue.substring(0, 30), 
              error: `Länkning misslyckades: ${bidirectionalError?.message || 'Okänt fel'}` 
            });
            errors++;
          }
        }
      }

      const messages: string[] = [];
      if (importedWords > 0) messages.push(`${importedWords} ord`);
      if (importedClues > 0) messages.push(`${importedClues} ledtrådar`);
      
      if (errors > 0) {
        toast.warning(`Importerade ${messages.join(', ')}. ${errors} fel uppstod.`);
      } else if (messages.length > 0) {
        toast.success(`Importerade ${messages.join(', ')}`);
      } else {
        toast.info('Inga nya ord eller ledtrådar att importera (redan importerade)');
      }
      
      fetchWords();
    } catch (error: any) {
      console.error('Error importing words:', error);
      toast.error(`Fel vid import: ${error.message}`);
    } finally {
      setImporting(false);
      setTimeout(() => setImportProgress(null), 3000);
    }
  };

  const blockSelected = async () => {
    if (selectedWords.size === 0) {
      toast.info('Välj minst ett ord att spärra');
      return;
    }

    setBlocking(true);
    try {
      const wordsToBlock = words.filter(w => selectedWords.has(w.id));
      const { data: { user } } = await supabase.auth.getUser();
      let blockedCount = 0;

      for (const word of wordsToBlock) {
        const wordUpper = word.word.toUpperCase();
        const { error } = await supabase.from('blocked_words').insert({
          word: word.word, word_upper: wordUpper, blocked_by: user?.id,
        });
        if (!error) blockedCount++;
      }

      toast.success(`Spärrade ${blockedCount} ord`);
      fetchWords();
    } catch (error: any) {
      console.error('Error blocking words:', error);
      toast.error(`Fel vid spärrning: ${error.message}`);
    } finally {
      setBlocking(false);
    }
  };

  const progressPercent = importProgress && importProgress.total > 0
    ? Math.round((importProgress.current / importProgress.total) * 100) 
    : 0;

  const getPhaseLabel = (phase: string) => {
    switch (phase) {
      case 'words': return 'Importerar ord...';
      case 'clues': return 'Importerar ledtrådar...';
      case 'bidirectional': return 'Skapar kopplingar...';
      default: return 'Bearbetar...';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Importera ord från gamla databasen</span>
          <Button variant="outline" size="sm" onClick={fetchWords} disabled={loading || importing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Uppdatera
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="bg-muted p-3 rounded-lg">
            <div className="text-muted-foreground">Totalt i gamla DB</div>
            <div className="text-2xl font-bold">{(stats.totalInOldDb || 0).toLocaleString()}</div>
          </div>
          <div className="bg-muted p-3 rounded-lg">
            <div className="text-muted-foreground">Ord i Supabase</div>
            <div className="text-2xl font-bold text-green-600">{(stats.totalWordsInSupabase || 0).toLocaleString()}</div>
          </div>
          <div className="bg-muted p-3 rounded-lg">
            <div className="text-muted-foreground">Ledtrådar i Supabase</div>
            <div className="text-2xl font-bold text-blue-600">{(stats.totalCluesInSupabase || 0).toLocaleString()}</div>
          </div>
          <div className="bg-muted p-3 rounded-lg">
            <div className="text-muted-foreground">Spärrade</div>
            <div className="text-2xl font-bold text-red-600">{(stats.blockedCount || 0).toLocaleString()}</div>
          </div>
        </div>

        {importProgress && (
          <div className="bg-muted/50 border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">{getPhaseLabel(importProgress.phase)}</span>
              <span className="text-muted-foreground">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-3" />
            <div className="text-xs text-muted-foreground truncate">
              {importProgress.currentWord}
            </div>
            <div className="flex gap-4 text-xs">
              <span className="text-green-600">✓ {importProgress.importedWords} ord</span>
              <span className="text-green-600">✓ {importProgress.importedClues} ledtrådar</span>
              {importProgress.errors > 0 && (
                <span className="text-red-600">✗ {importProgress.errors} fel</span>
              )}
            </div>
            {importProgress.failedItems.length > 0 && (
              <div className="mt-2 p-2 bg-destructive/10 rounded text-xs space-y-1">
                <div className="flex items-center gap-1 font-medium text-destructive">
                  <AlertCircle className="h-3 w-3" />
                  Misslyckade operationer:
                </div>
                {importProgress.failedItems.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="text-muted-foreground">
                    {item.word}{item.clue ? `: ${item.clue.substring(0, 20)}...` : ''} - {item.error}
                  </div>
                ))}
                {importProgress.failedItems.length > 5 && (
                  <div className="text-muted-foreground">...och {importProgress.failedItems.length - 5} till</div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-4 items-end flex-wrap">
          <div className="flex-1 relative min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Sök bland ord och ledtrådar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              disabled={loading || importing}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Språk</span>
            <Select
              value={String(selectedLanguage)}
              onValueChange={(value) => setSelectedLanguage(Number(value))}
              disabled={loading || importing}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Max ledtrådar</span>
            <Select
              value={String(maxClues)}
              onValueChange={(value) => {
                const newMaxClues = Number(value);
                setMaxClues(newMaxClues);
                setOffset(0);
                // Reset limit if switching between modes
                if (newMaxClues >= 0 && newMaxClues <= 5) {
                  setLimit(1000);
                } else {
                  setLimit(100);
                }
              }}
              disabled={loading || importing}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="-1">Alla</SelectItem>
                <SelectItem value="0">Utan ledtrådar</SelectItem>
                <SelectItem value="1">Max 1</SelectItem>
                <SelectItem value="5">Max 5</SelectItem>
                <SelectItem value="10">Max 10</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Antal resultat</span>
            <Select
              value={String(limit)}
              onValueChange={(value) => {
                setLimit(Number(value));
                setOffset(0);
              }}
              disabled={loading || importing}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {limitOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={selectAll} disabled={loading || importing}>
            Markera alla ord
          </Button>
          <Button variant="outline" size="sm" onClick={deselectAll} disabled={loading || importing}>
            Avmarkera allt
          </Button>
          <div className="flex-1" />
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={blockSelected} 
            disabled={loading || blocking || importing || selectedWords.size === 0}
          >
            {blocking ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
            Spärra ord ({selectedWords.size})
          </Button>
          <Button 
            size="sm" 
            onClick={importSelected} 
            disabled={loading || importing || (selectedWords.size === 0 && selectedClues.size === 0)}
          >
            {importing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Importera ({selectedWords.size} ord, {getSelectedCluesCount()} ledtrådar)
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredWords.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {words.length === 0 ? 'Inga fler ord att importera' : 'Inga ord matchar sökningen'}
          </div>
        ) : (
          <div className="border rounded-lg divide-y max-h-[500px] overflow-y-auto">
            {filteredWords.map((word) => (
              <div key={word.id} className="divide-y">
                <div className={`flex items-center gap-3 p-3 hover:bg-muted/50 ${selectedWords.has(word.id) ? 'bg-primary/10' : ''}`}>
                  <button onClick={() => toggleWordExpanded(word.id)} className="p-1 hover:bg-muted rounded" disabled={importing}>
                    {expandedWords.has(word.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                  </button>
                  <Checkbox checked={selectedWords.has(word.id)} onCheckedChange={() => toggleWord(word.id)} disabled={importing} />
                  <div className="flex-1 min-w-0"><span className="font-medium">{word.word}</span></div>
                  <div className="text-xs text-muted-foreground">{word.clues.length} ledtråd{word.clues.length !== 1 ? 'ar' : ''}</div>
                </div>

                {expandedWords.has(word.id) && word.clues.length > 0 && (
                  <div className="bg-muted/30 pl-12 pr-3 py-2 space-y-1">
                    <div className="flex gap-2 mb-2">
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => selectAllCluesForWord(word.id, word.clues)} disabled={importing}>
                        Markera alla ledtrådar
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => deselectAllCluesForWord(word.id, word.clues)} disabled={importing}>
                        Avmarkera alla
                      </Button>
                    </div>
                    {word.clues.map((clue, idx) => {
                      const clueKey = getClueKey(word.id, idx);
                      return (
                        <div 
                          key={idx}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted ${selectedClues.has(clueKey) ? 'bg-primary/10' : ''}`}
                          onClick={() => !importing && toggleClue(word.id, idx)}
                        >
                          <Checkbox checked={selectedClues.has(clueKey)} onCheckedChange={() => toggleClue(word.id, idx)} onClick={(e) => e.stopPropagation()} disabled={importing} />
                          <span className="text-sm">{clue}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0 || loading || importing}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Föregående
          </Button>
          <span className="text-sm text-muted-foreground">Visar {offset + 1} - {offset + words.length}</span>
          <Button variant="outline" size="sm" onClick={() => setOffset(offset + limit)} disabled={words.length < limit || loading || importing}>
            Nästa
            <ChevronRightIcon className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
